import React, { useContext } from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from 'react-router-dom';
import {
  ApolloProvider, ApolloClient, InMemoryCache, ApolloLink,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { AuthContext, AuthRoutes } from '@dexter-experts/auth-react';
import cleanTypenameLink from './utils/cleanTypenameLink';
import DataContext, { DataProvider } from './contexts/DataContext';
import { MessageProvider } from './contexts/MessageContext';
import MixpanelTracker, { mixpanelClient } from './utils/MixpanelTracker';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Landing from './pages/Landing';
import FirstTime from './pages/FirstTime';
import Tours from './pages/Tours';
import Home from './pages/Home';
import Search from './pages/Search';
import Projects from './pages/Projects';
import Project from './pages/Project';
import ProjectNew from './pages/ProjectNew';
import Schedule from './pages/Schedule';
import Settings from './pages/Settings';
import Meeting from './pages/Meeting';
import List from './pages/List';
import AiChat from './pages/AiChat';
// TODO: Add this import back after the FAQ page is done
// import FAQ from './pages/FAQ';

const NavbarRoute = ({ path, component: Component }) => {
  const { user } = useContext(DataContext);
  const onboardedData = user.settings.filter((s) => s.name === 'clientOnboarded')[0];
  const isOnboarded = onboardedData.value === 'true';
  if (!isOnboarded) {
    return <Redirect to="/tours/welcome" />;
  }
  return (
    <>
      <Navbar />
      <div className="main">
        <Route path={path}>
          <Component />
        </Route>
      </div>
      <Footer />
    </>
  );
};

const LoggedInRoutes = () => {
  const { user, team } = useContext(DataContext);
  const onboardedData = user.settings.filter((s) => s.name === 'clientOnboarded')[0];
  const isOnboarded = onboardedData.value === 'true';
  if (!team) {
    return (
      <Switch>
        <Route path="/" component={FirstTime} />
      </Switch>
    );
  }
  mixpanelClient.identify(user.id);
  // First time signup
  if (!isOnboarded) {
    MixpanelTracker.trackSignup(user, team);
  }
  MixpanelTracker.trackLogin(user, team);
  return (
    <Switch>
      <Route path="/tours/:page" component={Tours} />
      <NavbarRoute path="/search" component={Search} />
      <NavbarRoute path="/projects/new" component={ProjectNew} />
      <NavbarRoute path="/projects/:id/:tab?" component={Project} />
      <NavbarRoute path="/projects" component={Projects} />
      <NavbarRoute path="/lists/:id" component={List} />
      <NavbarRoute path="/settings/:page" component={Settings} />
      <NavbarRoute path="/schedule" component={Schedule} />
      <NavbarRoute path="/home" component={Home} />
      <NavbarRoute path="/meetings/:id" component={Meeting} />
      <NavbarRoute path="/ai-chat/:sessionId?" component={AiChat} />
      {/* TODO: Add the route after the FAQ page is done */}
      {/* <NavbarRoute path="/faq" component={FAQ} /> */}
      <Route
        path="/"
        render={() => <Redirect to={isOnboarded ? '/home' : '/tours/welcome'} />}
      />
    </Switch>
  );
};

const loggedInUserPages = (
  <DataProvider>
    <MessageProvider>
      <LoggedInRoutes />
    </MessageProvider>
  </DataProvider>
);

const guestUserPages = (
  <Switch>
    <Route path="/">
      <Landing />
    </Route>
  </Switch>
);

const AppRoutes = () => {
  const { authUser } = useContext(AuthContext);

  if (authUser?.emailVerified) {
    return <div>{loggedInUserPages}</div>;
  }

  return <div>{guestUserPages}</div>;
};

const Routes = () => {
  const { token } = useContext(AuthContext);
  const httpLink = createUploadLink({ uri: process.env.REACT_APP_GRAPHQL_BASE_URL });
  const authLink = setContext(async (_, { headers }) => ({
    headers: {
      ...headers,
      ...(token && { authorization: `Bearer ${token}` }),
    },
  }));

  const client = new ApolloClient({
    link: ApolloLink.from([cleanTypenameLink, authLink, httpLink]),
    cache: new InMemoryCache({
      typePolicies: {
        User: {
          merge: true,
          fields: {
            pendingJobs: { merge: false },
            upcomingMeetings: { merge: false },
            pastJobs: { merge: false },
            availability: { merge: false },
            managedProjects: { merge: false },
            externalAccounts: { merge: false },
          },
        },
        Team: {
          merge: true,
          fields: {
            members: { merge: false },
          },
        },
        Project: {
          merge: true,
          fields: {
            qualificationQuestions: { merge: false },
          },
        },
        Job: {
          merge: true,
          fields: {
            zoomMeeting: { merge: true },
          },
        },
        List: {
          fields: { experts: { merge: false } },
        },
        ExternalAccount: {
          fields: { subscribedCalendars: { merge: false } },
        },
        SearchFacet: {
          merge: true,
          fields: {
            items: {
              merge(_, incoming) {
                return incoming;
              },
            },
          },
        },
      },
    }),
  });

  return (
    <ApolloProvider client={client}>
      <Router>
        <Switch>
          <AuthRoutes />
          <Route component={AppRoutes} />
        </Switch>
      </Router>
    </ApolloProvider>
  );
};

export default Routes;
